import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { PARSED_ENV } from "@/constants/common";

/** The MCP server of this environment's API: what users paste into their AI client. */
export const MCP_SERVER_URL = `${PARSED_ENV.VITE_API_URL.replace(/\/$/, "")}/api/mcp`;

const CopyLine: FunctionComponent<{ value: string; label: string; testId: string }> = ({
	value,
	label,
	testId,
}) => {
	const { t } = useTranslation();
	const [copied, setCopied] = useState(false);

	const copy = async () => {
		try {
			await navigator.clipboard.writeText(value);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			// Without clipboard access the value is still there to select by hand.
		}
	};

	return (
		<div className="mt-1 flex items-center gap-2">
			<code
				className="flex-1 overflow-x-auto whitespace-nowrap rounded-lg border border-border bg-white px-3 py-2 text-[13px] text-primary-gray"
				data-testid={testId}
			>
				{value}
			</code>
			<button
				aria-label={label}
				className="shrink-0 rounded-lg border border-border bg-white px-3 py-2 text-[13px] font-semibold text-primary-gray hover:border-ink"
				type="button"
				onClick={copy}
			>
				{copied ? t("account.mcpConnections.guide.copied") : t("account.mcpConnections.guide.copy")}
			</button>
		</div>
	);
};

/**
 * How to connect an AI assistant to the scenario editor — BCM360-383. Shown with the connected
 * clients on the account page; open by itself while nothing is connected yet.
 */
export const McpGuide: FunctionComponent<{ openByDefault: boolean }> = ({ openByDefault }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.mcpConnections.guide.${key}`);
	const steps = (key: string) =>
		t(`account.mcpConnections.guide.${key}`, { returnObjects: true }) as string[];

	return (
		<details
			className="mt-4 rounded-xl border border-border bg-white px-4 py-3"
			data-testid="mcp-guide"
			open={openByDefault}
		>
			<summary className="cursor-pointer text-sm font-semibold text-primary-gray">
				{ts("title")}
			</summary>
			<div className="mt-3 space-y-4 text-sm leading-[20px] text-primary-gray">
				<p>{ts("intro")}</p>

				<div>
					<p className="font-semibold">{ts("addressTitle")}</p>
					<CopyLine label={ts("copyAddress")} testId="mcp-server-url" value={MCP_SERVER_URL} />
				</div>

				<div>
					<p className="font-semibold">{ts("claudeTitle")}</p>
					<ol className="mt-1 space-y-1">
						{steps("claudeSteps").map((step, index) => (
							<li key={step} className="flex gap-2">
								<span className="font-semibold">{index + 1}.</span>
								<span>{step}</span>
							</li>
						))}
					</ol>
				</div>

				<div>
					<p className="font-semibold">{ts("claudeCodeTitle")}</p>
					<CopyLine
						label={ts("copyCommand")}
						testId="mcp-claude-code-command"
						value={`claude mcp add --transport http bcm360 ${MCP_SERVER_URL}`}
					/>
					<p className="mt-1">{ts("claudeCodeAfter")}</p>
				</div>

				<div>
					<p className="font-semibold">{ts("chatgptTitle")}</p>
					<p className="mt-1">{ts("chatgptText")}</p>
				</div>

				<div>
					<p className="font-semibold">{ts("goodToKnowTitle")}</p>
					<ul className="mt-1 space-y-1">
						{steps("goodToKnow").map((line) => (
							<li key={line} className="flex gap-2">
								<span aria-hidden="true">•</span>
								<span>{line}</span>
							</li>
						))}
					</ul>
				</div>
			</div>
		</details>
	);
};
